import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Search, Eye, X } from 'lucide-react';
import { TableLoader } from '../../components/Common/Loader';
import { CustomDropdown } from '../../components/Common/CustomDropdown';
import { getCitiesAPI } from '../../api/locations';
import { getProductPricesAPI } from '../../api/products';
import type { ProductPriceItem } from '../../api/products';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';

interface CityOption {
  id: number;
  name: string;
}

export const PriceChange: React.FC = () => {
  const [cities, setCities] = useState<CityOption[]>([]);
  const [selectedCityName, setSelectedCityName] = useState<string>('Mumbai 1');
  const [selectedCityId, setSelectedCityId] = useState<number | null>(10);

  const [prices, setPrices] = useState<ProductPriceItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewingItem, setViewingItem] = useState<ProductPriceItem | null>(null);

  // 1. Fetch cities list on mount
  useEffect(() => {
    let isMounted = true;
    const fetchCities = async () => {
      try {
        const res = await getCitiesAPI();
        const list = res?.data || res?.cities || (Array.isArray(res) ? res : []);
        if (!isMounted) return;

        if (Array.isArray(list) && list.length > 0) {
          const mapped: CityOption[] = list.map((c: any) => ({
            id: c.id,
            name: c.name || `City ${c.id}`,
          }));
          setCities(mapped);

          // Select Mumbai 1 (id 10) by default if available, else first city
          const defaultCity = mapped.find((c) => c.id === 10 || c.name.toLowerCase().includes('mumbai 1')) || mapped[0];
          if (defaultCity) {
            setSelectedCityName(defaultCity.name);
            setSelectedCityId(defaultCity.id);
          }
        }
      } catch (err) {
        console.warn('Could not fetch cities list:', err);
      }
    };

    fetchCities();
    return () => {
      isMounted = false;
    };
  }, []);

  // 2. Fetch product prices when selectedCityId changes
  useEffect(() => {
    let isMounted = true;
    const fetchPrices = async () => {
      if (!selectedCityId) return;
      try {
        setLoading(true);
        const res = await getProductPricesAPI(selectedCityId);
        if (!isMounted) return;

        const dataList = res?.data || res?.prices || (Array.isArray(res) ? res : []);
        if (Array.isArray(dataList)) {
          setPrices(dataList);
        } else {
          setPrices([]);
        }
      } catch (err) {
        console.warn('Error fetching product prices:', err);
        if (isMounted) setPrices([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchPrices();
    return () => {
      isMounted = false;
    };
  }, [selectedCityId]);

  // Handle City Change
  const handleCityChange = (cityName: string) => {
    setSelectedCityName(cityName);
    const found = cities.find((c) => c.name === cityName);
    if (found) {
      setSelectedCityId(found.id);
    } else {
      setSelectedCityId(null);
    }
  };

  const formatDateOnly = (dateStr?: string | null): string => {
    if (!dateStr) return '-';
    if (dateStr.includes('T')) {
      return dateStr.split('T')[0];
    }
    return dateStr;
  };

  const formatEffectiveRange = (from?: string | null, to?: string | null): string => {
    if (!from && !to) return '-';
    const f = formatDateOnly(from);
    const t = formatDateOnly(to);
    return `${f} to ${t}`;
  };

  // Filtered prices based on search query
  const filteredPrices = useMemo(() => {
    if (!searchQuery.trim()) return prices;
    const q = searchQuery.toLowerCase();
    return prices.filter((item) => {
      const pName = item.product?.name || item.name || '';
      const pSapCode = item.product?.sapCode || item.sapCode || '';
      const cName = item.cityName || '';
      const zName = item.zoneName || '';
      return (
        pName.toLowerCase().includes(q) ||
        pSapCode.toLowerCase().includes(q) ||
        cName.toLowerCase().includes(q) ||
        zName.toLowerCase().includes(q)
      );
    });
  }, [prices, searchQuery]);

  // CSV Export
  const handleExportCSV = () => {
    const headers = [
      'Product ID',
      'SAP Code',
      'Product Name',
      'City Name',
      'Zone Name',
      'Effective Period',
      'MRP (INR)',
      'Distributor Margin',
      'Retailer Margin',
      'Trade Margin',
      'Distributor Price (INR)',
      'Retailer Price (INR)',
    ];

    const rows = filteredPrices.map((item) => [
      item.productId || item.product?.id || item.id,
      item.product?.sapCode || item.sapCode || '',
      item.product?.name || item.name || '',
      item.cityName || '',
      item.zoneName || '',
      formatEffectiveRange(item.effectiveFrom, item.effectiveTo),
      item.mrp ?? item.price ?? '',
      item.distributorMargin ?? item.DistributorMargin?.discount ?? '',
      item.retailerMargin ?? item.RetailerMargin?.discount ?? '',
      item.tradeMargin ?? item.TradeMargin?.discount ?? '',
      item.distributorPrice ?? '',
      item.retailerPrice ?? '',
    ]);

    exportToCSV(`product_prices_city_${selectedCityId || 'all'}`, headers, rows);
  };

  const cityOptionsList = useMemo(() => cities.map((c) => c.name), [cities]);

  return (
    <div className="flex flex-col gap-6 pb-6 select-none">
      {/* Header Row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
            title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Product Prices
          </h1>
        </div>

          <CSVButton onClick={handleExportCSV} />
      </div>

      {/* Filter Card Toolbar */}
      <div className="bg-white rounded-2xl p-5 border border-[#ECECF2] shadow-sm flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div className="w-full sm:w-72">
          <CustomDropdown
            label="Select City"
            placeholder="Select a City"
            options={cityOptionsList.length > 0 ? cityOptionsList : ['No Cities Available']}
            value={selectedCityName || 'Select a City'}
            onChange={handleCityChange}
          />
        </div>

        {/* Quick Search */}
        <div className="relative w-full sm:w-72">
          <label className="text-[11px] font-bold text-slate-700 block mb-1">Search</label>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-2.5 pointer-events-none" />
            <input
              type="text"
              placeholder="Search product name, SAP code, city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D]"
            />
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="bg-white rounded-3xl border border-[#ECECF2] shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FFFBEB] border-b border-amber-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                <th className="py-3.5 px-6">SAP Code</th>
                <th className="py-3.5 px-6">Product Name</th>
                <th className="py-3.5 px-6">City</th>
                <th className="py-3.5 px-6">Zone</th>
                <th className="py-3.5 px-6">Effective Period</th>
                <th className="py-3.5 px-6 text-right">MRP (₹)</th>
                <th className="py-3.5 px-6 text-right">Distributor Margin</th>
                <th className="py-3.5 px-6 text-right">Retailer Margin</th>
                <th className="py-3.5 px-6 text-right">Trade Margin</th>
                <th className="py-3.5 px-6 text-right">Distributor Price (₹)</th>
                <th className="py-3.5 px-6 text-right">Retailer Price (₹)</th>
                <th className="py-3.5 px-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
              {filteredPrices.map((item, idx) => {
                const sapCode = item.product?.sapCode || item.sapCode || '-';
                const name = item.product?.name || item.name || '-';
                const cityName = item.cityName || '-';
                const zoneName = item.zoneName || '-';
                const effectivePeriod = formatEffectiveRange(item.effectiveFrom, item.effectiveTo);
                const mrp = item.mrp ?? item.price;
                const distMargin = item.distributorMargin ?? item.DistributorMargin?.discount;
                const retMargin = item.retailerMargin ?? item.RetailerMargin?.discount;
                const tradeMargin = item.tradeMargin ?? item.TradeMargin?.discount;

                return (
                  <tr key={item.id || item.productId || idx} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-4 px-6 font-bold text-slate-800">{sapCode}</td>
                    <td className="py-4 px-6 font-semibold text-slate-800">{name}</td>
                    <td className="py-4 px-6 font-medium text-slate-700">{cityName}</td>
                    <td className="py-4 px-6 font-medium text-slate-700">{zoneName}</td>
                    <td className="py-4 px-6 text-slate-600 whitespace-nowrap">{effectivePeriod}</td>
                    <td className="py-4 px-6 text-right font-bold text-slate-800">
                      {mrp !== undefined && mrp !== null ? `₹${Number(mrp).toFixed(2)}` : '-'}
                    </td>
                    <td className="py-4 px-6 text-right font-medium text-emerald-700">
                      {distMargin !== undefined && distMargin !== null ? `${distMargin}` : '-'}
                    </td>
                    <td className="py-4 px-6 text-right font-medium text-blue-700">
                      {retMargin !== undefined && retMargin !== null ? `${retMargin}` : '-'}
                    </td>
                    <td className="py-4 px-6 text-right font-medium text-amber-700">
                      {tradeMargin !== undefined && tradeMargin !== null ? `${tradeMargin}` : '-'}
                    </td>
                    <td className="py-4 px-6 text-right font-extrabold text-[#C1272D]">
                      {item.distributorPrice !== undefined && item.distributorPrice !== null
                        ? `₹${Number(item.distributorPrice).toFixed(2)}`
                        : '-'}
                    </td>
                    <td className="py-4 px-6 text-right font-extrabold text-purple-700">
                      {item.retailerPrice !== undefined && item.retailerPrice !== null
                        ? `₹${Number(item.retailerPrice).toFixed(2)}`
                        : '-'}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <button
                        onClick={() => setViewingItem(item)}
                        title="View"
                        className="p-1.5 text-slate-500 hover:text-[#B71C1C] hover:bg-slate-100 rounded-lg transition-colors ml-auto flex items-center justify-center"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}

              {loading ? (
                <TableLoader colSpan={12} text="Loading product prices..." />
              ) : filteredPrices.length === 0 ? (
                <tr>
                  <td colSpan={12} className="py-12 text-center text-slate-400 font-medium">
                    {selectedCityId
                      ? 'No product price records found for this city.'
                      : 'Please select a city to view product prices.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Detail Modal */}
      {viewingItem && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-100 flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-xl font-bold text-[#C1272D]">Product Pricing Details</h2>
                <p className="text-xs text-slate-500 font-medium">
                  {viewingItem.product?.name || viewingItem.name || 'Product'} (SAP: {viewingItem.product?.sapCode || viewingItem.sapCode || '-'})
                </p>
              </div>
              <button
                onClick={() => setViewingItem(null)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center cursor-pointer transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Product & Location Summary Card */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                <div>
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">Product Details</span>
                  <p className="text-sm font-extrabold text-slate-800 mt-1">{viewingItem.product?.name || viewingItem.name}</p>
                  <p className="text-xs text-slate-600 font-medium mt-0.5">SAP Code: <span className="font-bold text-slate-800">{viewingItem.product?.sapCode || viewingItem.sapCode || '-'}</span></p>
                  <p className="text-xs text-slate-600 font-medium">Product ID: <span className="font-bold text-slate-800">{viewingItem.productId || viewingItem.product?.id}</span> | Pricing ID: <span className="font-bold text-slate-800">{viewingItem.id}</span></p>
                </div>

                <div>
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">Location & Validity</span>
                  <p className="text-xs text-slate-700 font-bold mt-1">City: <span className="font-semibold">{viewingItem.cityName || '-'}</span> (ID: {viewingItem.cityId || '-'})</p>
                  <p className="text-xs text-slate-700 font-bold">Zone: <span className="font-semibold">{viewingItem.zoneName || '-'}</span> (ID: {viewingItem.zoneId || '-'})</p>
                  <p className="text-xs text-slate-600 font-semibold mt-1">
                    Effective Period: <span className="font-bold text-[#C1272D]">{formatEffectiveRange(viewingItem.effectiveFrom, viewingItem.effectiveTo)}</span>
                  </p>
                </div>
              </div>

              {/* Price & Rates Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="bg-amber-50/60 border border-amber-100 p-4 rounded-2xl">
                  <span className="text-[11px] font-bold text-amber-800 uppercase block">MRP / Price</span>
                  <span className="text-xl font-extrabold text-amber-900 mt-1 block">
                    ₹{Number(viewingItem.mrp ?? viewingItem.price ?? 0).toFixed(2)}
                  </span>
                </div>

                <div className="bg-red-50/60 border border-red-100 p-4 rounded-2xl">
                  <span className="text-[11px] font-bold text-[#C1272D] uppercase block">Distributor Price</span>
                  <span className="text-xl font-extrabold text-[#C1272D] mt-1 block">
                    {viewingItem.distributorPrice !== undefined && viewingItem.distributorPrice !== null
                      ? `₹${Number(viewingItem.distributorPrice).toFixed(2)}`
                      : '-'}
                  </span>
                </div>

                <div className="bg-purple-50/60 border border-purple-100 p-4 rounded-2xl">
                  <span className="text-[11px] font-bold text-purple-800 uppercase block">Retailer Price</span>
                  <span className="text-xl font-extrabold text-purple-900 mt-1 block">
                    {viewingItem.retailerPrice !== undefined && viewingItem.retailerPrice !== null
                      ? `₹${Number(viewingItem.retailerPrice).toFixed(2)}`
                      : '-'}
                  </span>
                </div>
              </div>

              {/* Detailed Margins Section */}
              <div>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Margins Breakdown</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Distributor Margin */}
                  <div className="bg-emerald-50/60 border border-emerald-100 p-4 rounded-2xl space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-emerald-900">Distributor Margin</span>
                      <span className="text-xs font-extrabold px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-md">
                        {viewingItem.distributorMargin ?? viewingItem.DistributorMargin?.discount ?? '-'}
                      </span>
                    </div>
                    {viewingItem.DistributorMargin && (
                      <div className="text-[11px] text-slate-600 space-y-0.5 pt-1 border-t border-emerald-100/80">
                        <p>Margin ID: <span className="font-semibold">{viewingItem.DistributorMargin.id}</span></p>
                        <p>Discount: <span className="font-semibold">{viewingItem.DistributorMargin.discount}</span></p>
                        <p>Valid: <span className="font-semibold">{formatEffectiveRange(viewingItem.DistributorMargin.effectiveFrom, viewingItem.DistributorMargin.effectiveTo)}</span></p>
                      </div>
                    )}
                  </div>

                  {/* Retailer Margin */}
                  <div className="bg-blue-50/60 border border-blue-100 p-4 rounded-2xl space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-blue-900">Retailer Margin</span>
                      <span className="text-xs font-extrabold px-2 py-0.5 bg-blue-100 text-blue-800 rounded-md">
                        {viewingItem.retailerMargin ?? viewingItem.RetailerMargin?.discount ?? '-'}
                      </span>
                    </div>
                    {viewingItem.RetailerMargin && (
                      <div className="text-[11px] text-slate-600 space-y-0.5 pt-1 border-t border-blue-100/80">
                        <p>Margin ID: <span className="font-semibold">{viewingItem.RetailerMargin.id}</span></p>
                        <p>Discount: <span className="font-semibold">{viewingItem.RetailerMargin.discount}</span></p>
                        <p>Valid: <span className="font-semibold">{formatEffectiveRange(viewingItem.RetailerMargin.effectiveFrom, viewingItem.RetailerMargin.effectiveTo)}</span></p>
                      </div>
                    )}
                  </div>

                  {/* Trade Margin */}
                  <div className="bg-amber-50/60 border border-amber-100 p-4 rounded-2xl space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-amber-900">Trade Margin</span>
                      <span className="text-xs font-extrabold px-2 py-0.5 bg-amber-100 text-amber-800 rounded-md">
                        {viewingItem.tradeMargin ?? viewingItem.TradeMargin?.discount ?? '-'}
                      </span>
                    </div>
                    {viewingItem.TradeMargin && (
                      <div className="text-[11px] text-slate-600 space-y-0.5 pt-1 border-t border-amber-100/80">
                        <p>Margin ID: <span className="font-semibold">{viewingItem.TradeMargin.id}</span></p>
                        <p>Discount: <span className="font-semibold">{viewingItem.TradeMargin.discount}</span></p>
                        <p>Valid: <span className="font-semibold">{formatEffectiveRange(viewingItem.TradeMargin.effectiveFrom, viewingItem.TradeMargin.effectiveTo)}</span></p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Audit Timestamps */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 text-xs text-slate-500 flex flex-wrap justify-between gap-2">
                <span>Created At: <strong className="text-slate-700">{formatDateOnly(viewingItem.createdAt)}</strong></span>
                <span>Updated At: <strong className="text-slate-700">{formatDateOnly(viewingItem.updatedAt)}</strong></span>
                <span>Status: <strong className="text-slate-700">{viewingItem.status === 1 ? 'Active' : 'Inactive'}</strong></span>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-100 flex justify-end bg-slate-50 rounded-b-3xl">
              <button
                onClick={() => setViewingItem(null)}
                className="px-6 py-2 bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs rounded-xl cursor-pointer transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceChange;

